import { makeAutoObservable } from 'mobx'
import * as R from 'ramda'

export default class PPQuestion {
    questionId = ''

    kpiQuestionId = ''

    kpiQuestionShortText = ''

    kpiQuestionLongText = ''

    questionLongText = ''

    questionShortText = ''

    questionTypeCode = ''

    kpiType = ''

    surveyId = ''

    year = 0

    respondentType = ''

    chart = ''

    benchmark = []

    comparison = []

    comparisonYears = []

    questions = []

    filter = null

    hasNonNumericValues = false

    hasOtherOrExclusive = false

    hasNumericCodes = false

    settings = {
        sorting: 'noRanking',
        removeZero: false,
        removeBaseLimit: 0,
        excludeNonNumeric: false,
        locked: false,
        showAverage: false,
    }

    questionTypeCode = ''

    questionCoding = {
        labels: [],
        categories: [],
    }

    chosenCategories = new Map()

    position = 0

    pendingChanges = {
        category: '',
        labels: [],
        pct: 0,
    }

    constructor(props) {
        this.questionId = props.questionId ?? ''
        this.questionLongText = props.questionLongText ?? ''
        this.questionShortText = props.questionShortText ?? ''
        this.kpiQuestionId = props.kpiQuestionId ?? ''
        this.kpiQuestionShortText = props.kpiQuestionShortText ?? ''
        this.kpiQuestionLongText = props.kpiQuestionLongText ?? ''
        this.surveyId = props.surveyId ?? ''
        this.respondentType = props.respondentType ?? ''
        this.chart = props.chart ?? ''
        this.benchmark = props.benchmark?.slice() ?? []
        this.comparison = props.comparison?.slice() ?? []
        this.comparisonYears = props.comparisonYears ?? []
        this.hasOtherOrExclusive = props.hasOtherOrExclusive ?? false
        this.hasNumericCodes = props.hasNumericCodes ?? false
        this.filter = props.filter ?? null
        this.questions = props.questions ?? []
        this.settings = props.settings ?? {
            sorting: 'noRanking',
            removeZero: false,
            removeBaseLimit: 0,
            excludeNonNumeric: false,
            showAverage: props.questionTypeCode === 'OD',
            locked: false,
        }
        this.questionTypeCode = props.questionTypeCode ?? ''
        this.position = props.position ?? 0
        this.year = props.year ?? 0
        this.ppStore = props.ppStore
        this.store = props.store
        this.kpiType = props.kpiType
        makeAutoObservable(this)

        // if (['TA', 'TX'].includes(this.questionTypeCode)) {
        //     this.fetchData()
        // }
    }

    toJSON = () => ({
        questionId: this.questionId,
        questionLongText: this.questionLongText,
        questionShortText: this.questionShortText,
        kpiQuestionId: this.kpiQuestionId,
        kpiQuestionShortText: this.kpiQuestionShortText,
        kpiQuestionLongText: this.kpiQuestionLongText,
        surveyId: this.surveyId,
        respondentType: this.respondentType,
        chart: this.chart,
        benchmark: this.questionTypeCode === 'OD' ? [] : this.benchmark.slice(),
        comparison: this.questionTypeCode === 'OD' ? [] : this.comparison.slice(),
        filter: this.filter,
        settings: this.settings,
        questionTypeCode: this.questionTypeCode,
        questionCoding: Array.from(this.chosenCategories.values()).map(({ category, labels, pct }) => ({ name: category, comments: labels, pct })),
        position: this.position,
        questions: this.questions,
        year: this.year,
    })

    get codedCategories() {
        return this.questionCoding.categories.filter((c) => c.pct !== 0)
    }

    get codedLabels() {
        return this.questionCoding.labels
    }

    get categoryAtCurrentPosition() {
        return this.pendingChanges.category
    }

    get labelsForCategory() {
        return this.codedLabels.filter((cl) => cl.coding.has(this.categoryAtCurrentPosition))
    }

    get isVerbatim() {
        return ['TA', 'TX'].includes(this.questionTypeCode)
    }

    fetchData = async () => {
        const { store } = this
        const request = JSON.stringify({
            questionId: this.questionId,
            questionType: this.questionTypeCode,
            questionOptionId: null,
            filter: {
                state: [],
                surveyDataIds: [],
                question: {
                    ruleType: 'ruleGroup',
                    rules: [],
                },
                campaign: {
                    ruleType: 'ruleGroup',
                    rules: [],
                },
                responseDate: {
                    ruleType: 'ruleGroup',
                    rules: [],
                },
            },
        })

        const response = await store.http.post(`/report/v1/${this.surveyId}/textquestion`, request)

        const questionCoding = {}

        const allCategories = [...new Set(response.categories.flat())]
        console.log('all labels', allCategories)
        const labels = response.labels.map((label, index) => {
            const coding = R.pathOr([], ['categories', index], response)

            coding.forEach((category) => {
                questionCoding[category] ??= 0
                questionCoding[category] += 1
            })

            return {
                label,
                coding: new Set(coding),
            }
        })
        const otherKey = 'Misc/Other'
        const other = R.pick([otherKey], questionCoding)
        const total = Object.entries(questionCoding).reduce((a, [_, v]) => a + v, 0)
        const sortedQuestionCoding = Object.entries(R.omit([otherKey], questionCoding))
            .sort((a, b) => b[1] - a[1])
            .map(([k, v]) => ({
                label: k,
                value: k,
                pct: (v / (total ?? 1) * 100),
            }))

        this.questionCoding = {
            labels,
            categories: [...sortedQuestionCoding, { label: otherKey, value: otherKey, pct: (other[otherKey] ?? 0 / (total ?? 1) * 100) }],
        }
    }

    generateCoding = async () => {
        const { store } = this

        try {
            await store.http.post(`/report/v1/${this.surveyId}/textquestion/${this.questionId}/code`, JSON.stringify({}))
            await this.fetchData()
        } catch (e) {
            console.error(e)
            store.addNotification(null, 'Error loading question coding', 'error')
        }
    }

    handleChooseCategory = (category, pct) => {
        this.pendingChanges.category = category
        this.pendingChanges.pct = pct
        this.pendingChanges.labels = []
    }

    handleChooseLabel = (label) => {
        if (this.pendingChanges.labels.includes(label)) {
            this.pendingChanges.labels = this.pendingChanges.labels.filter((l) => l !== label)
        } else if (this.pendingChanges.labels.length === this.ppStore.verbatimSlots) {
            this.pendingChanges.labels.shift()
            this.pendingChanges.labels.push(label)
        } else {
            this.pendingChanges.labels.push(label)
        }
    }

    handleSaveVerbatimChoices = () => {
        this.chosenCategories.set(this.ppStore.verbatimPosition, this.pendingChanges)
        this.pendingChanges = {
            category: '',
            labels: [],
            pct: 0,
        }
        this.ppStore.handleCloseVerbatimSettingsModal()
    }

    handleCancelVerbatimChoices = () => {
        this.pendingChanges = {
            category: '',
            labels: [],
            pct: 0,
        }
        this.ppStore.handleCloseVerbatimSettingsModal()
    }

    handleCategoryToggle = (e) => {
        this.chosenCategories.push(e.name)
    }

    updateSettings = (event) => {
        this.settings[event.name] = event.value
    }

    updateChart = (event) => {
        this.chart = event.value
    }

    addComparison = (surveyId, year) => {
        if (this.comparison.includes(surveyId)) return
        this.comparison.push(surveyId)
        this.comparisonYears.push(year)
    }

    removeComparison = (surveyId, year) => {
        this.comparison = this.comparison.filter((c) => c !== surveyId)
        this.comparisonYears = this.comparisonYears.filter((y) => y !== year)
    }

    addBenchmark = (benchmark) => {
        if (this.benchmark.includes(benchmark)) return
        this.benchmark.push(benchmark)
    }

    removeBenchmark = (benchmark) => {
        this.benchmark = this.benchmark.filter((ben) => benchmark !== ben)
    }
}
